import { SHButton } from "@components/design-systems";
import { useNotification } from "@hooks/useNotification";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { AnalysisStatus } from "@models/platform-analysis/enums/status";
import { CompleteAnalysisDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/complete-analysis";
import { putPlatformAnalysisStatus } from "@services/platform-analysis/platformAnalysisService";
import { useState } from "react";

type AnalysisCompleteButtonProps = {
  disabled?: boolean;
  onSubmit?: () => Promise<unknown>;
};

export const AnalysisCompleteButton = ({
  disabled,
  onSubmit,
}: AnalysisCompleteButtonProps) => {
  const { notify } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { platformAnalysis, setIsCompleted, setIsReadOnly } =
    usePlatformAnalysis();

  const handleOnComplete = async () => {
    const analysisId = platformAnalysis?.id;
    if (!analysisId || !onSubmit) return;

    setIsSubmitting(true);

    const isSaved = await onSubmit();

    if (!isSaved) {
      setIsSubmitting(false);

      return;
    }

    const { isSuccess, data, message } = await putPlatformAnalysisStatus(
      analysisId,
      AnalysisStatus.Completed,
    );

    if (isSuccess && data) {
      notify(message, {
        variant: "success",
        close: true,
      });
      setIsReadOnly(true);
      setIsCompleted(true);
      setShowDialog(false);
    } else {
      notify(message, {
        variant: "error",
        close: true,
      });
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <SHButton
        variant="contained"
        size="extraMedium"
        isLoading={isSubmitting}
        disabled={isSubmitting || disabled}
        onClick={() => setShowDialog(true)}
      >
        Complete
      </SHButton>
      {showDialog && (
        <CompleteAnalysisDialog
          isSubmitting={isSubmitting}
          onSubmit={handleOnComplete}
          onClose={() => setShowDialog(false)}
        />
      )}
    </>
  );
};
